import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import {
  useBoardEditingStore,
  useWallContentStore,
  useWallParticipantsStore,
  type WallCard,
} from '@walls/store';
import { getBoardCardTree } from '../selectors/boardCardSelectors';

interface BoardNotificationProps {
  cards: WallCard[];
}

export function BoardNotification({ cards }: BoardNotificationProps) {
  const [hasEvaluatedInitialVisibility, setHasEvaluatedInitialVisibility] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);
  const editingMode = useBoardEditingStore((state) => state.editingMode);
  const boardContentLoaded = useWallContentStore((state) => state.boardContentLoaded);
  const isLoadingBoardContent = useWallContentStore((state) => state.isLoadingBoardContent);
  const { rootCards, rootClusters } = getBoardCardTree(cards);
  const boardIsEmpty = rootCards.length === 0 && rootClusters.length === 0;

  useEffect(() => {
    if (hasEvaluatedInitialVisibility || !boardContentLoaded || isLoadingBoardContent) {
      return;
    }

    setIsVisible(Boolean(currentParticipantId) && boardIsEmpty);
    setHasEvaluatedInitialVisibility(true);
  }, [
    boardContentLoaded,
    boardIsEmpty,
    currentParticipantId,
    hasEvaluatedInitialVisibility,
    isLoadingBoardContent,
  ]);

  useEffect(() => {
    if (isVisible && (!boardIsEmpty || editingMode === 'create-card')) {
      setIsVisible(false);
    }
  }, [boardIsEmpty, editingMode, isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      id="card-notification"
      sx={{
        alignItems: 'center',
        bgcolor: '#e6273c',
        borderRadius: '5px',
        boxShadow: '0 2px 5px #c3c3c3',
        color: '#fff',
        display: 'flex',
        fontWeight: 'bold',
        left: 20,
        minHeight: 50,
        pl: '15px',
        pr: '41px',
        py: '15px',
        position: 'absolute',
        top: 90,
        zIndex: 99999,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: '20px' }}>
        Type or double click to create a card
      </Typography>
      <IconButton
        id="close-card-notification"
        aria-label="Dismiss card notification"
        size="small"
        onClick={() => setIsVisible(false)}
        sx={{
          color: 'inherit',
          height: 20,
          p: 0,
          position: 'absolute',
          right: 10,
          top: 14,
          width: 20,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
